import { useEffect, useState } from "react";
import { BackArrow, SaveIcon } from "allIcons";
import {
  AddComponent,
  BodyContainer,
  TextEditor,
  HeaderContainer,
  IconWrapper,
  InputField,
  PreviewCard,
  DeleteButton,
  MainButton,
  SecondaryHeading,
  RightMainContainer,
  PrimaryHeading,
  Loader,
  PopOver,
  DropDownCard,
  SelectField,
} from "commonUI";
import { useParams } from "react-router-dom";
import {
  useAddGlossary,
  useDeleteGlossary,
  useGetGlossaryCategory,
  useGlosssary,
  useUpdateGlossary,
} from "api/templateLibrary/glossary";
import { useTags } from "api/siteSettings/Tags";
import {
  getUserSavePermission,
  isEmpty,
  isEqual,
  parse,
  useUtilities,
} from "helpers";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function AddEditGlossary({
  previousPath = `/${ModulePrefixes.TEMPLATE_LIBRARY}/glossary`,
  isSiteTemplate,
}) {
  const { notification, navigate } = useUtilities();
  const { id } = useParams();
  const [inputValues, setInputValues] = useState({
    glossary_title: "",
    glossary_body: "",
    glossary_integration: "",
    tags: [],
    category_id: "",
  });
  const [openTags, setOpenTags] = useState(false);
  const { data: glossary, isLoading: glossaryLoading } = useGlosssary();
  const { mutate: addGlossary, isLoading: isAdding } = useAddGlossary();
  const { mutate: updateGlossary, isLoading: isUpdating } = useUpdateGlossary();
  const { mutate: deleteGlossary, isLoading: isDeleting } = useDeleteGlossary();
  const { data: tags } = useTags();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();
  const { data: categories } = useGetGlossaryCategory();

  useEffect(() => {
    if (id === "add" || glossaryLoading || glossary.length === 0) return;
    const glossaryData = glossary.filter(
      (item) => String(item.id) === String(id)
    )[0];

    if (isEmpty(glossaryData)) {
      navigate(previousPath);
      return;
    }
    setInputValues({
      glossary_title: glossaryData.glossary_title ?? "",
      glossary_body: glossaryData.glossary_body ?? "",
      glossary_integration: glossaryData.glossary_integration ?? "",
      tags: parse(glossaryData.tags ?? "[]"),
      category_id: glossaryData.category_id ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glossaryLoading, id, glossary]);

  function handleSave() {
    if (inputValues.glossary_title === "") {
      notification({
        type: "error",
        message: t("common.input.error.titleRequired"),
      });
      return;
    }
    if (id === "add") {
      addGlossary({ ...inputValues });
      return;
    }
    updateGlossary({ ...inputValues, glossary_id: id });
  }

  return (
    <>
      <RightMainContainer>
        {(isAdding || isUpdating || isDeleting) && <Loader loader="block" />}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow onClick={() => navigate(previousPath)} />
            <PrimaryHeading className="clamp-text">
              {isEqual(id, "add")
                ? t("libraryTemplate.glossary.addGlossary")
                : inputValues.glossary_title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={!isSiteTemplate && !isEqual(id, "add") && delete_templates}
              onDelete={() => deleteGlossary(id)}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={handleSave}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("common.input.title.label")}
            placeholder={t("common.input.title.placeholder")}
            value={inputValues.glossary_title}
            setValue={(value) =>
              setInputValues((pre) => ({
                ...pre,
                glossary_title: value,
              }))
            }
          />
          <TextEditor
            label={t("libraryTemplate.glossary.input.body.label")}
            placeholder={t("libraryTemplate.glossary.input.body.placeholder")}
            editorClass="mt-2"
            value={inputValues.glossary_body}
            reRender={
              id === "add" ? false : inputValues.glossary_body ? true : false
            }
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, glossary_body: value }))
            }
          />
          <SelectField
            label={t("libraryTemplate.glossary.input.category.label")}
            placeholder={t(
              "libraryTemplate.glossary.input.category.placeholder"
            )}
            value={inputValues.category_id}
            setValue={(value) =>
              setInputValues((pre) => ({ ...pre, category_id: value }))
            }
            className="mt-3 mb-3"
          >
            {categories?.map((ele) => (
              <option value={ele.id}>{ele.title}</option>
            ))}
          </SelectField>
          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {inputValues.tags.map((tag) => (
                <PreviewCard
                  color={tag.color}
                  title={tag.text}
                  icon={true}
                  className="m-0"
                  onCross={() =>
                    setInputValues((pre) => ({
                      ...pre,
                      tags: pre.tags.filter(
                        (_) => String(_.id) !== String(tag.id)
                      ),
                    }))
                  }
                />
              ))}
            </div>
          </div>
          <AddComponent
            title={t("common.input.tags.addTag")}
            value={openTags}
            onClick={() => setOpenTags(true)}
          >
            <PopOver
              open={openTags}
              onClose={() => setOpenTags(false)}
              style={{
                top: "auto",
                left: "auto",
                right: "auto",
                bottom: "calc(100% + 15px)",
              }}
              toolTip={{ vertical: "bottom", horizontal: "center" }}
            >
              <DropDownCard
                title={t("common.input.tags.label")}
                listStyle
                filterSelectedItems
                selectedValues={inputValues.tags.map((tag) => tag.id)}
                list={tags?.map((tag) => ({
                  ...tag,
                  text: tag.tag_title,
                  index: tag.id,
                }))}
                onSelect={(value) =>
                  inputValues.tags.filter(
                    (_) => String(_.id) === String(value.id)
                  ).length === 0 &&
                  setInputValues((pre) => ({
                    ...pre,
                    tags: [
                      ...pre.tags,
                      {
                        id: value.id,
                        text: value.tag_title,
                        color: value.color,
                      },
                    ],
                  }))
                }
              />
            </PopOver>
          </AddComponent>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
